/**
 * Osaamisrekisteri — jaetut käsitteet ja tilalogiikka
 * (docs/KEHITYSSUUNNITELMAT.md, luku 6).
 *
 * Neljäs moduuli, ja se on täällä samasta syystä kuin koulutukset:
 * järjestelmä tuntee jo käyttäjät, roolit ja sähköpostin. Moduulissa on
 * kaksi kokonaisuutta, joilla on eri lukijat ja eri elinkaari —
 * **osaamisrekisteri** (lopputulos, tarjousliitteen aineistoa) ja
 * **kehityssuunnitelma** (prosessi, esihenkilön ja työntekijän välistä).
 * Tämä tiedosto on rekisterin puoli; suunnitelman puoli tulee vaiheessa 2.
 *
 * ## Mitä tässä tiedostossa on
 *
 * Tilalogiikka **puhtaina funktioina**: onko saavutus voimassa,
 * vanhenemassa vai mennyt, ja mikä on vanhenemispäivä. Samat selaimessa ja
 * palvelimella, ja testattavissa ilman kantaa — sama jako kuin
 * koulutuksilla (`trainings.ts`), ja samasta syystä: kaksi toteutusta
 * kysymykselle "onko tämä voimassa" eriytyisi, ja eron huomaisi vasta
 * siitä, että raportti ja muistutus ovat eri mieltä.
 */
import { daysInMonth, formatIsoDate, parseIsoDate } from './time.js';
/**
 * `daysUntil` on yleinen kalenteripäivien erotus, joka sattuu asumaan
 * koulutusten tiedostossa. Se tuodaan tänne eikä kirjoiteta uudelleen:
 * millisekunneista laskettu ero heittäisi kesäajan vaihtuessa, ja kaksi
 * toteutusta samalle kysymykselle eriytyisi juuri siinä kohdassa.
 */
import { daysUntil } from './trainings.js';
// --- Rajat -----------------------------------------------------------------
/**
 * Sertifikaattikoodi on tunniste, ei nimi: `AZ-104`, `SAA-C03`,
 * `CKA`. Neljäkymmentä merkkiä riittää pisimmillekin (`PCNSE`,
 * `1Z0-1085-23`), eikä se houkuttele kirjoittamaan koko nimeä koodiksi.
 */
export const SERTIFIKAATTI_KOODI_MAX = 40;
export const SERTIFIKAATTI_NIMI_MAX = 200;
/** Tarjoaja tekstinä eikä luettelona: uusi tarjoaja ei saa olla migraatio. */
export const SERTIFIKAATTI_TARJOAJA_MAX = 80;
/**
 * Kuinka monta päivää ennen vanhenemista saavutus on "vanhenemassa".
 *
 * Yhdeksänkymmentä päivää, ei kolmekymmentä: Microsoftin ilmainen renewal
 * aukeaa kuusi kuukautta ennen vanhenemista, ja AWS:n uusintatentille
 * pitää ehtiä varata aika. Kuukausi olisi muistutus, joka tulee liian
 * myöhään ollakseen muuta kuin harmillinen.
 */
export const SERTIFIKAATTI_VAROITUS_PAIVAA = 90;
// --- Tila ------------------------------------------------------------------
/**
 * Saavutuksen tila tiettynä päivänä.
 *
 * `VOIMASSA` kattaa myös sen, joka ei vanhene koskaan
 * (`expiresOn = null`). Erillistä "ikuinen"-tilaa ei ole: jokainen
 * raportti joutuisi päättämään erikseen, lasketaanko se voimassa olevaksi,
 * ja vastaus olisi joka kerta kyllä.
 */
export const SERTIFIKAATTI_TILAT = ['VOIMASSA', 'VANHENEMASSA', 'VANHENTUNUT'];
export function sertifikaatinTila(saavutus, tanaan) {
    if (saavutus.expiresOn === null)
        return 'VOIMASSA';
    const paivia = daysUntil(tanaan, saavutus.expiresOn);
    // Vanhenemispäivä itse on vielä voimassa: todistuksessa lukeva päivä on
    // viimeinen voimassaolopäivä, ei ensimmäinen vanhentunut. Sama tulkinta
    // kuin koulutussuorituksella (`isTrainingValid`).
    if (paivia < 0)
        return 'VANHENTUNUT';
    return paivia <= SERTIFIKAATTI_VAROITUS_PAIVAA ? 'VANHENEMASSA' : 'VOIMASSA';
}
export function onVoimassa(saavutus, tanaan) {
    return sertifikaatinTila(saavutus, tanaan) !== 'VANHENTUNUT';
}
// --- Vanhenemispäivä -------------------------------------------------------
/**
 * Oletusvanheneminen: suorituspäivä + luettelon voimassaolokuukaudet.
 *
 * **Sama laskutapa kuin koulutussuorituksella** (`trainingExpiryDate`),
 * tarkoituksella rivi riviltä: kuun viimeisen päivän ylivuoto typistetään
 * (31.8. + 6 kk on 28.2., ei 3.3.), ja vanhenemispäivä itse on vielä
 * voimassa. Kaksi eri laskentaa samalle kysymykselle olisi kaksi eri
 * vastausta, ja eron huomaisi vasta siitä, että koulutusmatriisi ja
 * osaamisraportti ovat eri mieltä samasta kuukausimäärästä.
 *
 * Tämä on **oletus, ei sääntö**: kutsuja saa antaa oman päivän. Tarjoajat
 * laskevat eri tavoin — Microsoft siirtää renewalin vuodella
 * uusimispäivästä, AWS kolme vuotta tentistä — ja rivillä pitää olla se
 * päivä, joka todistuksessa lukee, ei se, jonka tämä funktio arvasi.
 */
export function oletusVanhenemisPaiva(achievedOn, validityMonths) {
    if (validityMonths === null)
        return null;
    const { year, month, day } = parseIsoDate(achievedOn);
    const kuukausiaYhteensa = year * 12 + (month - 1) + validityMonths;
    const uusiVuosi = Math.floor(kuukausiaYhteensa / 12);
    const uusiKuukausi = (kuukausiaYhteensa % 12) + 1;
    return formatIsoDate(uusiVuosi, uusiKuukausi, Math.min(day, daysInMonth(uusiVuosi, uusiKuukausi)));
}
// --- Uusin saavutus --------------------------------------------------------
/**
 * Sertifikaatin **nykyinen** tila henkilöllä, kun rivejä voi olla monta.
 *
 * Uusinta on uusi rivi eikä vanhan päivitys (luku 6), joten "onko AZ-104
 * voimassa" ei ole yhden rivin kysymys. Vastaus on se rivi, joka on
 * voimassa pisimpään — ei viimeksi suoritettu.
 *
 * Ero on todellinen: joku voi uusia sertifikaatin ennen vanhenemista, ja
 * silloin uudempi rivi on suoritettu myöhemmin **ja** vanhenee myöhemmin.
 * Mutta joku voi myös kirjata jälkikäteen vanhan suorituksen, ja silloin
 * viimeksi suoritettu on se, joka vanhenee ensin. Raportin pitää kertoa,
 * mihin asti osaaminen on todistettavissa.
 *
 * Ikuinen (`expiresOn = null`) voittaa aina.
 */
export function voimassaolevinSaavutus(saavutukset) {
    let paras = null;
    for (const saavutus of saavutukset) {
        if (paras === null) {
            paras = saavutus;
            continue;
        }
        if (paras.expiresOn === null)
            continue;
        if (saavutus.expiresOn === null || saavutus.expiresOn > paras.expiresOn) {
            paras = saavutus;
        }
    }
    return paras;
}
//# sourceMappingURL=osaaminen.js.map